import type { ReactNode } from 'react';
import { useState } from 'react';
import { Button } from '@/ui/button';
import { Input } from '@/ui/input';
import { Label } from '@/ui/label';
import {
  buildCentralAuthRedirectTarget,
  isLocalDevHost,
} from './auth-screen-redirect';

const CENTRAL_AUTH_URL = 'https://tos.run/login';

export function isLocalDev() {
  return (
    typeof window !== 'undefined' && isLocalDevHost(window.location.hostname)
  );
}

export interface AuthScreenProps {
  icon: ReactNode;
  title: string;
  subtitle: string;
  /** Where to redirect on success. Caller should resolve ?returnTo from URL. */
  returnTo?: string;
  onLogin: (email: string, password: string) => Promise<{ error?: string }>;
}

export function AuthScreen({
  icon,
  title,
  subtitle,
  returnTo = '/',
  onLogin,
}: AuthScreenProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (!isLocalDev()) {
    const redirectTarget =
      typeof window !== 'undefined'
        ? buildCentralAuthRedirectTarget({
            currentHref: window.location.href,
            returnTo,
          })
        : '';
    window.location.href = `${CENTRAL_AUTH_URL}?redirect=${encodeURIComponent(redirectTarget)}`;
    return (
      <main className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-muted-foreground">正在跳转到登录页面...</p>
      </main>
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const result = await onLogin(email, password);
      if (result.error) {
        setError(result.error);
        setLoading(false);
        return;
      }
      window.location.assign(returnTo);
    } catch {
      setError('操作失败，请重试');
      setLoading(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4 py-8">
      <div className="w-full max-w-4xl overflow-hidden rounded-2xl border bg-card shadow-xl">
        <div className="grid lg:grid-cols-[1fr_1fr]">
          {/* Left: Branding */}
          <div className="flex flex-col justify-between bg-primary p-8 text-primary-foreground lg:p-10">
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-foreground/15 text-lg font-semibold">
                  {icon}
                </div>
                <div>
                  <h1 className="text-xl font-semibold">{title}</h1>
                  <p className="text-sm text-primary-foreground/70">
                    {subtitle}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 space-y-3 text-sm text-primary-foreground/70">
              <p>使用分配的账号登录系统</p>
              <p>如无账号，请联系管理员开通</p>
            </div>
          </div>

          {/* Right: Login Form */}
          <div className="p-8 lg:p-10">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-foreground">登录</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                输入邮箱与密码进入工作台
              </p>
            </div>

            <form className="grid gap-4" onSubmit={handleSubmit}>
              <div className="grid gap-2">
                <Label htmlFor="auth-email">邮箱</Label>
                <Input
                  id="auth-email"
                  aria-label="邮箱"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="输入邮箱"
                  required
                  autoFocus
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="auth-password">密码</Label>
                <Input
                  id="auth-password"
                  aria-label="密码"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="输入密码"
                  minLength={8}
                  required
                />
              </div>
              {error && (
                <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                  {error}
                </div>
              )}
              <Button type="submit" disabled={loading}>
                {loading ? '登录中...' : '登录'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </main>
  );
}
